import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import axios from 'axios';

interface GeoserviceInfoResponse {
    result: GeoserviceInfo|null;
}

interface GeoserviceInfo {
    country: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        iso_code: string;
    };
    continent: {
        code: string;
    };
}

@Component
export class GeoIp extends ThemeComponent {
    @Prop currentCountry!: string;

    @Ref() closeButton!: HTMLElement[];
    @Ref({ required: false }) contentBlock!: HTMLElement[];

    private availableAlternativeCountries: string[] = [
        'GB',
        'US'
    ];

    async init() {

        // Check session storage
        const preventDisplay = sessionStorage.getItem('preventGeoIpDisplay');
        if (preventDisplay) {
            return;
        }

        // Get location data from ESC geo app
        const locationData = await this.getLocationData();
        if (locationData === null) {
            this.$log.error(`Error whilst getting location data`);
            return;
        }

        // Check location and show/hide correct Promo Bar
        this.updateContentVisibility(locationData);
    }

    async getLocationData(): Promise<GeoserviceInfo|null> {
        // You can set _geoservice_ip in your local storage to pretend you're visting from another country
        // instructions: https://developer.chrome.com/docs/devtools/storage/localstorage/
        // EXAMPLES:
        // New York, US: 72.229.28.185
        // Madrid, Spain: 95.100.115.77
        // Lisbon, Portugal: 104.89.164.91
        // Cremona, Italy: 81.88.228.195

        const geoIpOverride = window.localStorage.getItem('_geoservice_ip');
        const response = await axios.get<GeoserviceInfoResponse>('https://geo.eastsideapps.io/api/v1/info', {
            params: {
                ip: geoIpOverride || undefined,
            }
        });

        if (response.status === 200) {
            return response.data.result;
        }
        return null;
    }

    updateContentVisibility(locationData: GeoserviceInfo) {

        // Get specific data
        const country = locationData.country.iso_code;
        // Check if country is current
        if (country === this.currentCountry || !this.availableAlternativeCountries.includes(country)) {
            sessionStorage.setItem('preventGeoIpDisplay', 'true');
            return;
        }

        // Listen for hide popup events
        this.listenForHidePopupEvents();

        // Update content based on country
        for (const contentBlock of this.contentBlock) {
            if (contentBlock.getAttribute('data-country-code') == country) {
                contentBlock.classList.add('geo-ip__content-block--active');
            }
        }

        // Show popup
        this.classList.add('geo-ip--active');
    }

    listenForHidePopupEvents() {

        // Close button
        for (const closeButton of this.closeButton) {
            this.$listen(closeButton, 'click', (e) => {

                this.closePopup();
            });
        }

        // Outer element
        this.$listen(this, 'click', (e) => {
            if (e.target && this.classList.contains('geo-ip--active')) {
                const clickedElement = e.target as HTMLElement;
                if (clickedElement === this) {
                    this.closePopup();
                }
            }
        });
    }

    closePopup() {

        sessionStorage.setItem('preventGeoIpDisplay', 'true');
        this.classList.remove('geo-ip--active');
    }
}
